// src/layout/Navbar.jsx
import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  InputBase,
  Avatar,
  Menu,
  MenuItem,
  Button,
  useMediaQuery,
  useTheme,
  Chip,
  Divider,
} from "@mui/material";
import {
  MenuRounded,
  MenuOpen,
  Search as SearchIcon,
  Logout as LogoutIcon,
  Clear,
  Download as DownloadIcon,
  Smartphone,
  Login as LoginIcon,
} from "@mui/icons-material";
import colors from "../constants/colors";
import okmarketlogo from "../assets/okmarketlogo.png";
import {useNavigate} from "react-router-dom";
const Navbar = ({
  user,
  onLogout,
  onToggleSidebar,
  sidebarOpen,
  onSearchChange,
  searchText,
  onLoginClick,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [searchLocal, setSearchLocal] = React.useState(searchText || "");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const navigate = useNavigate();
  //const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  // Sync local state with prop
  React.useEffect(() => {
    setSearchLocal(searchText || "");
  }, [searchText]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onSearchChange && onSearchChange(searchLocal);
    }, 400); // 400ms debounce to reduce API calls
    return () => clearTimeout(timeout);
  }, [searchLocal, onSearchChange]);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleMenuClose();
    onLogout();
  };
 const handleLogoClick = () => {
    navigate("/market");
  };
  const handleClearSearch = () => {
    setSearchLocal("");
    onSearchChange("");
  };

  const handleGetApp = () => {
    window.open(
      "https://play.google.com/store/apps/details?id=com.ChaitanyaAgro.Okmarket",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleLogin = () => {
    onLoginClick();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        background: colors.white,
        backdropFilter: "blur(12px)",
        boxShadow: "none",
        borderBottom: `1px solid ${colors.borderGray}`,
        height: "70px",
        justifyContent: "center",
        color: colors.darkBlack,
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1, sm: 2, md: 3 },
          gap: { xs: 1, sm: 2 },
        }}
      >
        {/* 🟢 Left Section - Logo Only */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, ml: -1.5 }}>
          <IconButton
            onClick={onToggleSidebar}
            sx={{
              color: colors.green,
              backgroundColor: colors.softMint,
              "&:hover": { backgroundColor: colors.hoverMint },
              borderRadius: "14px",
              p: { xs: 0.7, sm: 1 },
              transition: "all 0.25s ease",
            }}
          >
            {sidebarOpen ? (
              <MenuOpen sx={{ fontSize: { xs: 22, sm: 26 } }} />
            ) : (
              <MenuRounded sx={{ fontSize: { xs: 22, sm: 26 } }} />
            )}
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1rem", sm: "1.3rem" },
              display: "flex",
              alignItems: "center",
              gap: 0.8,
            }}
          >
            <Box
              component="img"
              onClick={handleLogoClick}
              src={okmarketlogo}
              alt="OKMarket"
              sx={{
                width: { xs: 48, sm: 60 },
                height: { xs: 48, sm: 60 },
                borderRadius: "10px",
                objectFit: "cover",
                cursor: "pointer",
              }}
            />
          </Typography>
        </Box>

        {/* 🟡 Right Section - Search, Get App & User/Login */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            flexShrink: 0,
            flexWrap: "nowrap",
            ml: "auto",
          }}
        >
          {/* 🔍 Search bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: colors.white,
              borderRadius: 3,
              px: { xs: 1, sm: 1.5 },
              py: { xs: 0.4, sm: 0.6 },
              width: isMobile ? "110px" : isTablet ? "200px" : "250px",
              border: `1.5px solid ${colors.green}`,
              "&:focus-within": {
                border: `2px solid ${colors.darkGreen}`,
                boxShadow: `0 0 5px ${colors.green}`,
              },
              transition: "all 0.25s ease",
              position: "relative",
              flexShrink: 0,
            }}
          >
            <SearchIcon
              sx={{
                color: colors.green,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                mr: 0.5,
              }}
            />
            <InputBase
              placeholder="Search..."
              value={searchLocal}
              onChange={(e) => setSearchLocal(e.target.value)}
              sx={{
                flex: 1,
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                color: colors.darkGray,
                "& input::placeholder": { color: colors.midGray },
                pr: searchLocal ? 2 : 0,
              }}
            />
            {searchLocal && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{
                  position: "absolute",
                  right: 4,
                  color: colors.midGray,
                  "&:hover": {
                    color: colors.darkGray,
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                  padding: 0.5,
                }}
              >
                <Clear sx={{ fontSize: "1rem" }} />
              </IconButton>
            )}
          </Box>

          {/* 📏 Vertical Divider 1 */}
          {!isMobile && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: colors.borderGray,
                borderWidth: "1px",
                height: "30px",
                my: "auto",
              }}
            />
          )}

          {/* 📱 Get App Button - Responsive */}
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={isTablet ? <Smartphone /> : <DownloadIcon />}
              onClick={handleGetApp}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: { sm: 1.5, md: 2 },
                py: { sm: 0.8, md: 0.9 },
                fontSize: { sm: "0.8rem", md: "0.85rem", lg: "0.9rem" },
                fontWeight: 600,
                background: `linear-gradient(135deg, ${colors.green} 0%, ${colors.darkGreen} 100%)`,
                whiteSpace: "nowrap",
                minWidth: "auto",
                boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
                  background: `linear-gradient(135deg, ${colors.darkGreen} 0%, ${colors.green} 100%)`,
                },
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {isTablet ? "Get App" : "Download App"}
            </Button>
          )}

          {/* 📱 Mobile Get App Chip */}
          {isMobile && (
            <Chip
              icon={<Smartphone sx={{ fontSize: "16px !important" }} />}
              label="App"
              onClick={handleGetApp}
              size="small"
              sx={{
                backgroundColor: colors.green,
                color: colors.white,
                fontWeight: 600,
                fontSize: "0.75rem",
                height: "32px",
                "&:hover": {
                  backgroundColor: colors.darkGreen,
                },
                "& .MuiChip-icon": {
                  color: colors.white,
                  fontSize: "16px",
                  marginLeft: "8px",
                },
              }}
            />
          )}

          {/* 📏 Vertical Divider 2 */}
          {user && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: colors.borderGray,
                borderWidth: "1px",
                height: "30px",
                my: "auto",
                display: { xs: "none", sm: "block" },
              }}
            />
          )}

          {/* 👤 Avatar & Menu OR Login Button */}
          {/* 👤 Avatar & Menu OR Login Button */}
          {user ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.6, sm: 1 },
                cursor: "pointer",
                overflow: "hidden",
                flexShrink: 0,
                pl: { xs: 0, sm: 1 },
              }}
              onClick={handleMenuOpen}
            >
              <Avatar
                src={user?.photoURL || ""}
                sx={{
                  width: { xs: 38, sm: 44 },
                  height: { xs: 38, sm: 44 },
                  bgcolor: colors.lightGreen,
                  color: colors.green,
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", sm: "1.05rem" },
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() ||
                  user?.email?.charAt(0)?.toUpperCase()}
              </Avatar>

              {!isMobile && (
                <Box
                  sx={{
                    textAlign: "left",
                    lineHeight: 1.1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.8rem", sm: "0.95rem" },
                      color: colors.darkBlack,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user?.name || "Admin"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      color: colors.darkGray,
                      opacity: 0.8,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user?.email || "admin11s@okmarket.com"}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Button
              variant="contained"
              onClick={handleLogin}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: { xs: 1, sm: 3 },
                py: { xs: 0.8, sm: 1 },
                fontSize: { xs: "0rem", sm: "0.9rem" }, // Hide text on mobile
                fontWeight: 600,
                background: `linear-gradient(135deg, ${colors.green} 0%, ${colors.darkGreen} 100%)`,
                whiteSpace: "nowrap",
                minWidth: { xs: "40px", sm: "auto" }, // Fixed width for icon on mobile
                width: { xs: "40px", sm: "auto" }, // Fixed width for icon on mobile
                height: { xs: "40px", sm: "auto" }, // Fixed height for icon on mobile
                boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(76, 175, 80, 0.4)",
                  background: `linear-gradient(135deg, ${colors.darkGreen} 0%, ${colors.green} 100%)`,
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Show only icon on mobile, icon + text on larger screens */}
              <LoginIcon
                sx={{
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  mr: { xs: 0, sm: 1 }, // No margin on mobile, margin on larger screens
                }}
              />
              <Box sx={{ display: { xs: "none", sm: "block" } }}>Login</Box>
            </Button>
          )}

          {/* ⚙️ Dropdown Menu */}
          {user && (
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  mt: 1,
                  overflow: "hidden",
                  minWidth: 140,
                  backgroundColor: colors.lightRed,
                  "& .MuiList-root": {
                    padding: "4px 0",
                  },
                },
              }}
            >
              <MenuItem
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: "#ffebee",
                  },
                  "&.Mui-focusVisible": {
                    backgroundColor: "transparent",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "transparent !important",
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: "#ffebee",
                  },
                }}
                disableRipple
              >
                <LogoutIcon sx={{ mr: 1, color: colors.red }} />
                <Typography sx={{ color: colors.red, fontWeight: 500 }}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
